import { Link } from 'react-router-dom'
import { SCHEDULES_PATH } from '@config/queueConfig'
import { fmtInFuture, fmtWhenInZone } from './queueFormat'
import { firmTextClass, reportRef } from './jobIdentity'

/** How many upcoming runs to list before deferring to the schedules page. */
const MAX_ROWS = 5

/**
 * The runs that are GOING to happen, on the Queue page's Scheduled tab.
 *
 * ## The defect this fixes (QA, 2026-09-03)
 *
 * The Scheduled tab matched `job.effectiveState === 'scheduled' | 'recurring'`.
 * Those are real states — a "queue for later" job sits in `scheduled` until the
 * cron promotes it — but they are also *transient*, and a **recurring schedule
 * never produces one**: `runDueSchedules` inserts its job already `queued` and it
 * reaches `ready` within seconds. Measured on QA D1: **zero** rows on vladdemo
 * have ever held `state='scheduled'`, against **11 schedules**, 6 of them enabled
 * with a future `next_run_at`.
 *
 * So the tab read `Scheduled 0` and offered "Nothing in the queue matches that
 * filter" — three inches below this page's own header chip reading "Manage
 * schedules · 10" and a subtitle reading "next scheduled run in 20 hours". One
 * screen contradicting itself about whether anything is scheduled.
 *
 * ## Why a separate list rather than fake job rows
 *
 * A schedule is not a run. It has no duration, no row count, no result to open
 * and no `id` the drawer could resolve — so mapping one into `QueueTable` would
 * mean five columns of em-dashes and a row whose "View report" cannot work. The
 * heading states the distinction instead, and the two populations sit one above
 * the other under the tab whose label covers both.
 *
 * ## What is deliberately NOT shown
 *
 * - **Paused and dormant schedules.** They are not upcoming runs. Their counts
 *   already live in the header chip beside "Manage schedules", which is also
 *   where you go to act on them.
 * - **A `nextRunAt` in the past.** That means the cron has not caught up, not
 *   that a run is imminent — the same rule `fmtInFuture` enforces by returning
 *   null, and the same one the page's subtitle uses.
 * - **Anything at all when the schedules read did not resolve.** The caller must
 *   not render this component then: an empty list derived from a read that did
 *   not happen is the empty-vs-failed conflation this page has been fixed for
 *   twice. The page's own "Schedule status unknown" band covers that case.
 */
/*
 * `now` is REQUIRED, with no `Date.now()` default.
 *
 * Two reasons, and the lint rule (react-hooks/purity) is right about the first:
 * a `Date.now()` call in render makes this list's boundary depend on when React
 * happened to re-render, so a row could appear or vanish on an unrelated parent
 * update. The page already owns a 30-second clock and passes it, which is also
 * what keeps this list consistent with the subtitle's "next scheduled run in N
 * hours" derived from the same value.
 *
 * Second, a missing clock means "cannot judge whether anything is upcoming", and
 * the honest render for that is nothing — a default of 0 would pass every past
 * fire time through the `> now` filter and advertise runs that already happened.
 */
const UpcomingScheduledRuns = ({ schedules = [], now }) => {
  if (!Number.isFinite(now)) return null

  const upcoming = schedules
    .filter((s) => s.enabled && !s.needsSignIn && s.nextRunAt != null && s.nextRunAt > now)
    .sort((a, b) => a.nextRunAt - b.nextRunAt)

  if (upcoming.length === 0) return null
  const shown = upcoming.slice(0, MAX_ROWS)

  return (
    <section
      data-testid="upcoming-scheduled-runs"
      aria-labelledby="upcoming-runs-heading"
      className="border-b border-gray-100 px-3.5 py-3"
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="upcoming-runs-heading" className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
          Upcoming scheduled runs
        </h3>
        <Link to={SCHEDULES_PATH} className="text-[11.5px] font-semibold text-cb-accent-text hover:underline">
          Manage schedules
        </Link>
      </div>

      <ul className="divide-y divide-gray-100">
        {shown.map((s) => (
          <li key={s.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
            <span className="min-w-0">
              {/* Spec 216: an Advanced Report schedule's name defaults to the firm's report
                  name — kept out of Pendo — and its id is never printed. */}
              <span className={`text-[12.5px] font-medium text-cb-navy ${firmTextClass(s.reportId)}`.trim()}>{s.name || 'Untitled schedule'}</span>
              {reportRef(s.reportId) ? <span className="ml-1.5 text-[11.5px] text-gray-500">{reportRef(s.reportId)}</span> : null}
              {s.description ? <span className="ml-1.5 text-[11.5px] text-gray-500">· {s.description}</span> : null}
            </span>
            <span className="shrink-0 text-[12.5px] text-gray-800">
              {/* The schedule's OWN zone, with its abbreviation — a schedule fires
                  at its local wall time, and rendering it in the viewer's clock is
                  how one screen ends up saying 7:00 AM and 8:00 AM about the same
                  run (see fmtWhenInZone). */}
              {fmtWhenInZone(s.nextRunAt, s.timezone)}
              <span className="ml-1.5 text-[11.5px] text-gray-500">{fmtInFuture(s.nextRunAt, now)}</span>
            </span>
          </li>
        ))}
      </ul>

      {upcoming.length > shown.length && (
        <p className="mt-1.5 text-[11.5px] text-gray-500">
          <Link to={SCHEDULES_PATH} className="font-semibold text-cb-accent-text hover:underline">
            {upcoming.length - shown.length} more upcoming
          </Link>
          {' '}on the schedules page.
        </p>
      )}

      <p className="mt-2 text-[11.5px] text-gray-500">
        These haven&apos;t run yet, so there is nothing to open. Each one appears below as a
        finished run once it fires.
      </p>
    </section>
  )
}

export default UpcomingScheduledRuns
